import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const playerKpiSchema = z.object({
    averageKpi: z.number(),
    averageKpiPreviousTournament: z.number().nullable(),
    totalKpi: z.number(),
    totalKpiPreviousTournament: z.number().nullable(),
    previousTournamentName: z.string().nullable(),
});

const matchSummarySchema = z.object({
    wins: z.number(),
    losses: z.number(),
    winsOvertime: z.number(),
    lossesOvertime: z.number(),
});

const topPlayerSchema = z.object({
    playerId: z.number(),
    name: z.string(),
    kpiScore: z.number(),
    matchesPlayed: z.number(),
});

const lastMatchSchema = z.object({
    matchId: z.number().optional(),
    opponent: z.string(),
    score: z.string(),
    date: z.string(),
    result: z.string(),
});

const teamStatsSchema = z.object({
    shotAccuracy: z.number(),
    faceOffWinPercentage: z.number(),
    savePercentage: z.number(),
    puckContestWinPercentage: z.number(),
    corsiPercentage: z.number(),
    fenwickPercentage: z.number(),
});

const homeAwayComparisonSchema = z.object({
    winRate: z.number(),
    goalsPerGame: z.number(),
    goalsAgainstPerGame: z.number(),
    corsiPercentage: z.number(),
    fenwickPercentage: z.number(),
    gamesPlayed: z.number(),
    recordWin: z.string(),
});

const recentFormSchema = z.object({
    matchId: z.number(),
    opponentTeamId: z.number(),
    opponentTeamName: z.string(),
    result: z.string(),
    score: z.string(),
    date: z.string(),
});

const teamPenaltiesSchema = z.object({
    penaltyId: z.number(),
    penaltyPoints: z.number(),
    reason: z.string(),
});

const standingsShema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    position: z.number(),
    location: z.string(),
    mp: z.number(),
    w: z.number(),
    wo: z.number(),
    lo: z.number(),
    l: z.number(),
    goalsScored: z.number(),
    goalsConceded: z.number(),
    totalPoints: z.number(),
    recentForm: z.array(recentFormSchema).optional().default([]),
    teamPenalties: z.array(teamPenaltiesSchema).optional().default([]),
});

const standingsSchema = z.object({
    tournamentId: z.number(),
    tournamentName: z.string(),
    overallStandings: z.array(standingsShema).optional(),
    homeStandings: z.array(standingsShema).optional(),
    awayStandings: z.array(standingsShema).optional(),
});

const teamIdentitySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
});

const kpiInfoSchema = z.object({
    avgKpi: z.number(),
    sumKpi: z.number(),
});

const distanceStatsSchema = z.object({
    distanceTravelled: z.number(),
    distanceTravelledWithPuck: z.number(),
    forwardDistanceTravelledWithPuck: z.number(),
});

const teamShotsStatsSchema = z.object({
    shots: z.number(),
    shotsOnGoal: z.number(),
    shotsWithScreen: z.number(),
    goals: z.number(),
    saves: z.number(),
    expectedGoalsSum: z.number(),
});

const opponentStatsSchema = z.object({
    opponentShots: z.number(),
    opponentShotsOnGoal: z.number(),
    opponentGoals: z.number(),
    opponentSaves: z.number(),
    opponentExpectedGoalsSum: z.number(),
});

const passStatsSchema = z.object({
    successfulPasses: z.number(),
    allPasses: z.number(),
    totalPassDistance: z.number(),
    forwardPassDistance: z.number(),
});

const puckStatsSchema = z.object({
    puckContestsWon: z.number(),
    puckContestsLost: z.number(),
    totalPuckContests: z.number(),
    puckControlDuration: z.number(),
});

const faceoffStatsSchema = z.object({
    faceoffWins: z.number(),
    faceoffCount: z.number(),
});

const advancedStatsSchema = z.object({
    corsiFor: z.number(),
    corsiAgainst: z.number(),
    corsiForPercentage: z.number(),
    fenwickFor: z.number(),
    fenwickAgainst: z.number(),
    fenwickForPercentage: z.number(),
    pdo: z.number(),
});

const powerplayStatsSchema = z.object({
    powerplayGoals: z.number(),
    powerplayCount: z.number(),
});

const penaltyKillStatsSchema = z.object({
    penaltyKillGoalsAgainst: z.number(),
    penaltyKillCount: z.number(),
});

const teamLeaderboardSchema = z.object({
    teamIdentity: teamIdentitySchema,
    kpiInfo: kpiInfoSchema,
    distanceStats: distanceStatsSchema,
    teamShotsStats: teamShotsStatsSchema,
    opponentStats: opponentStatsSchema,
    passStats: passStatsSchema,
    puckStats: puckStatsSchema,
    faceoffStats: faceoffStatsSchema,
    advancedStats: advancedStatsSchema,
    powerplayStats: powerplayStatsSchema,
    penaltyKillStats: penaltyKillStatsSchema,
});

const identitySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    playerId: z.number(),
    playerName: z.string(),
    role: z.string(),
    matchesPlayed: z.number(),
});

const goalkeeperIdentitySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    goalKeeperId: z.number(),
    goalKeeperName: z.string(),
    role: z.string(),
    matchesPlayed: z.number(),
});

const skatingStatsSchema = z.object({
    topSpeed: z.number(),
    timeOnIce: z.number(),
    distanceTravelled: z.number(),
    averageSpeedWithPuck: z.number(),
    accelerations: z.number(),
    decelerations: z.number(),
});

const puckControlStatsSchema = z.object({
    puckControlTime: z.number(),
    puckControlDistance: z.number(),
    puckControlForwardDistance: z.number(),
    puckContestsWon: z.number(),
    puckContestsLost: z.number(),
    puckContestParticipations: z.number(),
    faceOffCount: z.number(),
    faceOffWins: z.number(),
});

const actionStatsSchema = z.object({
    plus: z.number(),
    minus: z.number(),
    plusMinusTotal: z.number(),
    shots: z.number(),
    goals: z.number(),
    fastestShotSpeed: z.number().nullable(),
    offensiveScreens: z.number(),
    blockedShots: z.number(),
});

const playerPassingStatsSchema = z.object({
    allPassesUnderPressure: z.number(),
    successfulPassesUnderPressure: z.number(),
    allPassesUnderHighPressure: z.number(),
    successfulPassesUnderHighPressure: z.number(),
    averagePressureReceived: z.number(),
    pressuredOpponentsPasses: z.number(),
    averagePressureGiven: z.number(),
    allPasses: z.number(),
    successfulPasses: z.number(),
    receivedPasses: z.number(),
    totalPassDistance: z.number(),
    forwardPassDistance: z.number(),
});

const playerAdvancedStatsSchema = z.object({
    corsiFor: z.number(),
    corsiAgainst: z.number(),
    corsiForPercentage: z.number(),
    relativeCorsiForPercentage: z.number(),
    fenwickForPercentage: z.number(),
    relativeFenwickForPercentage: z.number(),
    fullStrengthSavePercentage: z.number(),
    fullStrengthShootingPercentage: z.number(),
    pdo: z.number(),
});

const goalkeeperPerformanceStatsSchema = z.object({
    timeOnIce: z.number(),
    saves: z.number(),
    goalsAgainst: z.number(),
    goalsAgainstAverage: z.number(),
    savePercentage: z.number(),
});

const goalkeeperPassingStatsSchema = z.object({
    successfulPasses: z.number(),
    allPasses: z.number(),
});

const goalkeeperWithScreenStatsSchema = z.object({
    shotsAgainstWithScreen: z.number(),
    goalsAgainstWithScreen: z.number(),
});

const playersLeaderboardSchema = z
    .object({
        identity: identitySchema,
        kpiInfo: kpiInfoSchema,
        skatingStats: skatingStatsSchema,
        puckControlStats: puckControlStatsSchema,
        actionStats: actionStatsSchema,
        playerPassingStats: playerPassingStatsSchema,
        advancedMetrics: playerAdvancedStatsSchema,
    })
    .transform(({ kpiInfo, ...rest }) => ({
        ...rest,
        kpiInfo: {
            overall: { position: "Overall", minKpi: 0, avgKpi: kpiInfo.avgKpi, maxKpi: 0 },
            byPosition: [] as { position: string; minKpi: number; avgKpi: number; maxKpi: number }[],
        },
    }));

const goalkeepersLeaderboardSchema = z
    .object({
        goalKeeperIdentity: goalkeeperIdentitySchema,
        kpiInfo: kpiInfoSchema,
        performanceStats: goalkeeperPerformanceStatsSchema,
        passingStats: goalkeeperPassingStatsSchema,
        withScreenStats: goalkeeperWithScreenStatsSchema,
    })
    .transform(({ kpiInfo, ...rest }) => ({
        ...rest,
        kpiInfo: {
            overall: { position: "GK", minKpi: 0, avgKpi: kpiInfo.avgKpi, maxKpi: 0 },
            byPosition: [] as { position: string; minKpi: number; avgKpi: number; maxKpi: number }[],
        },
    }));

const tournamentLeaderboardSchema = z.object({
    teams: z.array(teamLeaderboardSchema),
});

const tournamentPlayersLeaderboardSchema = z.object({
    players: z.array(playersLeaderboardSchema),
});

const tournamentgoalkeepersLeaderboardSchema = z.object({
    goalKeepers: z.array(goalkeepersLeaderboardSchema),
});

export type TopPlayer = z.infer<typeof topPlayerSchema>;
export type LastMatch = z.infer<typeof lastMatchSchema>;
export type PlayerKpi = z.infer<typeof playerKpiSchema>;
export type MatchSummary = z.infer<typeof matchSummarySchema>;
export type TeamStats = z.infer<typeof teamStatsSchema>;
export type HomeAwayComparison = z.infer<typeof homeAwayComparisonSchema>;
export type TeamIdentity = z.infer<typeof teamIdentitySchema>;
export type DistanceStats = z.infer<typeof distanceStatsSchema>;
export type TeamShotsStats = z.infer<typeof teamShotsStatsSchema>;
export type OpponentStats = z.infer<typeof opponentStatsSchema>;
export type PassStats = z.infer<typeof passStatsSchema>;
export type PuckStats = z.infer<typeof puckStatsSchema>;
export type FaceoffStats = z.infer<typeof faceoffStatsSchema>;
export type AdvancedStats = z.infer<typeof advancedStatsSchema>;
export type PowerplayStats = z.infer<typeof powerplayStatsSchema>;
export type PenaltyKillStats = z.infer<typeof penaltyKillStatsSchema>;
export type TeamLeaderboard = z.infer<typeof teamLeaderboardSchema>;
export type TournamentLeaderboard = z.infer<typeof tournamentLeaderboardSchema>;
export type PlayersLeaderboard = z.infer<typeof playersLeaderboardSchema>;
export type GoalkeepersLeaderboard = z.infer<typeof goalkeepersLeaderboardSchema>;
export type TournamentPlayersLeaderboard = z.infer<typeof tournamentPlayersLeaderboardSchema>;
export type TournamentGoalkeepersLeaderboard = z.infer<typeof tournamentgoalkeepersLeaderboardSchema>;
export type Standings = z.infer<typeof standingsSchema>;
export type StandingTeam = z.infer<typeof standingsShema>;

const fetchTournamentLeaderboard = async (tournamentId: number): Promise<TournamentLeaderboard> => {
    try {
        const response = await axiosInstance.get(`/${tournamentId}/leaderboard`);
        return tournamentLeaderboardSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch tournament leaderboard: ${error}`);
    }
};

const fetchTournamentPlayersLeaderboard = async (tournamentId: number): Promise<TournamentPlayersLeaderboard> => {
    try {
        const response = await axiosInstance.get(`/${tournamentId}/leaderboard/players`);
        return tournamentPlayersLeaderboardSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch tournament players leaderboard: ${error}`);
    }
};

const fetchTournamentGoalkeepersLeaderboard = async (
    tournamentId: number
): Promise<TournamentGoalkeepersLeaderboard> => {
    try {
        const response = await axiosInstance.get(`/${tournamentId}/leaderboard/goalkeepers`);
        return tournamentgoalkeepersLeaderboardSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch tournament goalkeepers leaderboard: ${error}`);
    }
};

export const tournamentLeaderboardQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["tournament-leaderboard", tournamentId],
        queryFn: () => fetchTournamentLeaderboard(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

export const tournamentPlayersLeaderboardQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["tournament-players-leaderboard", tournamentId],
        queryFn: () => fetchTournamentPlayersLeaderboard(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

export const tournamentGoalkeepersLeaderboardQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["tournament-goalkeepers-leaderboard", tournamentId],
        queryFn: () => fetchTournamentGoalkeepersLeaderboard(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

const teamAnalysisSummarySchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    playerKpi: playerKpiSchema,
    matchSummary: matchSummarySchema,
    previousMatchSummary: matchSummarySchema,
});

const teamAnalysisMatchesSchema = z.object({
    upcomingMatch: lastMatchSchema.nullable().optional(),
    lastMatches: z.array(lastMatchSchema),
});

const teamAnalysisPerformanceSchema = z.object({
    teamStats: teamStatsSchema,
    homeAwayComparison: z.object({
        home: homeAwayComparisonSchema,
        away: homeAwayComparisonSchema,
    }),
    standings: standingsSchema.nullable().optional(),
});

export type TeamAnalysisSummary = z.infer<typeof teamAnalysisSummarySchema>;
export type TeamAnalysisMatches = z.infer<typeof teamAnalysisMatchesSchema>;
export type TeamAnalysisPerformance = z.infer<typeof teamAnalysisPerformanceSchema>;

export const teamSummaryQueryOptions = (tournamentId: number, teamId: number) =>
    queryOptions({
        queryKey: ["team-analysis-summary", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId}/analysis/summary`);
            return teamAnalysisSummarySchema.parse(response.data);
        },
        enabled: !!tournamentId && !!teamId,
        staleTime: 5 * 60 * 1000,
    });

export const teamMatchesQueryOptions = (tournamentId: number, teamId: number) =>
    queryOptions({
        queryKey: ["team-analysis-matches", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId}/analysis/matches`);
            return teamAnalysisMatchesSchema.parse(response.data);
        },
        enabled: !!tournamentId && !!teamId,
        staleTime: 5 * 60 * 1000,
    });

export const teamTopPlayersQueryOptions = (tournamentId: number, teamId: number) =>
    queryOptions({
        queryKey: ["team-analysis-top-players", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId}/analysis/top-players`);
            return z.array(topPlayerSchema).parse(response.data);
        },
        enabled: !!tournamentId && !!teamId,
        staleTime: 5 * 60 * 1000,
    });

export const teamPerformanceQueryOptions = (tournamentId: number, teamId: number) =>
    queryOptions({
        queryKey: ["team-analysis-performance", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId}/analysis/performance`);
            return teamAnalysisPerformanceSchema.parse(response.data);
        },
        enabled: !!tournamentId && !!teamId,
        staleTime: 5 * 60 * 1000,
    });

const teamAnalysisHomeAwaySchema = z.object({
    teamStats: teamStatsSchema,
    homeAwayComparison: z.object({
        home: homeAwayComparisonSchema,
        away: homeAwayComparisonSchema,
    }),
});

export type TeamAnalysisHomeAway = z.infer<typeof teamAnalysisHomeAwaySchema>;

export const teamHomeAwayQueryOptions = (tournamentId: number, teamId: number) =>
    queryOptions({
        queryKey: ["team-analysis-home-away", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId}/analysis/home-away`);
            return teamAnalysisHomeAwaySchema.parse(response.data);
        },
        enabled: !!tournamentId && !!teamId,
        staleTime: 5 * 60 * 1000,
    });

export const teamStandingsByLocationQueryOptions = (
    tournamentId: number,
    location: "overall" | "home" | "away",
    teamId?: number
) =>
    queryOptions({
        queryKey: ["tournament-standings-by-location", tournamentId, location, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/standings/${location}`);
            return z.array(standingsShema).parse(response.data);
        },
        enabled: !!tournamentId && teamId !== undefined,
        staleTime: 5 * 60 * 1000,
    });

export const tournamentTopPlayersQueryOptions = (tournamentId: number, teamId?: number) =>
    queryOptions({
        queryKey: ["tournament-top-players", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/analysis/top-players`);
            return z.array(topPlayerSchema).parse(response.data);
        },
        enabled: !!tournamentId && teamId !== undefined,
        staleTime: 5 * 60 * 1000,
    });

export const tournamentMatchesQueryOptions = (tournamentId: number, teamId?: number) =>
    queryOptions({
        queryKey: ["tournament-matches", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/analysis/matches`);
            return teamAnalysisMatchesSchema.parse(response.data);
        },
        enabled: !!tournamentId && teamId !== undefined,
        staleTime: 5 * 60 * 1000,
    });

export const tournamentSummaryQueryOptions = (tournamentId: number, teamId?: number) =>
    queryOptions({
        queryKey: ["tournament-summary", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/analysis/summary`);
            return teamAnalysisSummarySchema.parse(response.data);
        },
        enabled: !!tournamentId && teamId !== undefined,
        staleTime: 5 * 60 * 1000,
    });

export const tournamentHomeAwayQueryOptions = (tournamentId: number, teamId?: number) =>
    queryOptions({
        queryKey: ["tournament-home-away", tournamentId, teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/analysis/home-away`);
            return teamAnalysisHomeAwaySchema.parse(response.data);
        },
        enabled: !!tournamentId && teamId !== undefined,
        staleTime: 5 * 60 * 1000,
    });
